/**
 * @file        src/pages/wallet/WalletSettings.jsx
 * @description Global wallet controls: feature kill-switch, self-recharge bounds,
 *              and the checkout hold TTL.
 * @author      Kinetika Labs
 * @created     2026-07-23
 * @changelog
 *   - 2026-07-23 Claude — Initial (Admin Wallet Management).
 *   - 2026-08-03 Claude — Crumb: Account Management -> Wallet Management (new
 *                          sidebar group).
 *   - 2026-09-21 Claude — "Default grant expiry (days)" removed (client). The
 *                          expiry of an admin grant is picked per grant on the
 *                          credit form; this tenant-wide fallback was a second
 *                          way to say the same thing and is not wanted. Backend
 *                          field, repo column and DB column go with it (admin
 *                          migration 064), so a blank date box means "never
 *                          expires" — what the credit form already claims.
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import TableSkeleton from '../../components/TableSkeleton';
import { toast } from '../../lib/toast';
import { qk } from '../../api/queryKeys';
import { getWalletSettings, updateWalletSettings } from '../../api/wallets';

export default function WalletSettings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: qk.wallet.settings, queryFn: getWalletSettings });
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (data?.data) {
      const s = data.data;
      setForm({
        featureEnabled: !!s.featureEnabled,
        minRecharge: s.minRecharge ?? 0,
        maxRecharge: s.maxRecharge ?? 0,
        holdTtlMinutes: s.holdTtlMinutes ?? 20,
      });
    }
  }, [data]);

  const mut = useMutation({
    mutationFn: (body) => updateWalletSettings(body),
    onSuccess: () => { toast.ok('Saved', 'Wallet settings updated.'); qc.invalidateQueries({ queryKey: qk.wallet.settings }); },
    onError: (e) => toast.err('Failed', e?.response?.data?.error?.message || 'Could not save settings.'),
  });

  function submit(e) {
    e.preventDefault();
    mut.mutate({
      featureEnabled: form.featureEnabled,
      minRecharge: Number(form.minRecharge),
      maxRecharge: Number(form.maxRecharge),
      holdTtlMinutes: Number(form.holdTtlMinutes),
    });
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  return (
    <>
      <PageHead title="Wallet Settings" subtitle="Wallet Management · global wallet controls" />
      <article className="panel glass" style={{ maxWidth: 640 }}>
        {isLoading || !form ? <TableSkeleton /> : (
          <form className="form-grid" onSubmit={submit}>
            <label className="field full" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" checked={form.featureEnabled} onChange={set('featureEnabled')} style={{ width: 'auto' }} />
              <span>Wallet enabled <span className="muted">(off = customers cannot spend or recharge)</span></span>
            </label>

            <div className="field">
              <label>Checkout hold TTL (minutes)</label>
              <input type="number" min="1" max="1440" value={form.holdTtlMinutes} onChange={set('holdTtlMinutes')} />
            </div>
            <div className="field">
              <label>Minimum recharge (₹)</label>
              <input type="number" min="0" step="0.01" value={form.minRecharge} onChange={set('minRecharge')} />
            </div>
            <div className="field">
              <label>Maximum recharge (₹)</label>
              <input type="number" min="1" step="0.01" value={form.maxRecharge} onChange={set('maxRecharge')} />
            </div>

            <div className="form-actions full">
              <button className="btn primary" type="submit" disabled={mut.isPending}>{mut.isPending ? 'Saving…' : 'Save settings'}</button>
            </div>
          </form>
        )}
      </article>
    </>
  );
}
